import React, { useState, Fragment, useMemo, useEffect, useCallback, memo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Divider from './Divider';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';

import { connect } from 'react-redux';

import { SidebarLoader } from './SkeletonLoaders';

import { InfoTooltip, Button, TextField } from 'components/v3';
import Drawer from 'components/Drawer';

import { veezooRoutes } from 'components/app/routes';

import flatMap from 'array.prototype.flatmap';

import SidebarList from './SidebarList';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import clsx from 'clsx';
import styles from './styles.scss';
import { KnowledgeGraphIcon } from 'components/Icons';
import { LinkWithQuery } from 'components/LinkWithQuery';

import { trackEvent } from 'utils/eventTracking';
import { SIDEBAR_EXPANDED_SIZE, layouts } from 'config/constants';
import { useSidebar } from 'hooks';

export const KnowledgeGraphSidebarDrawerWidth = SIDEBAR_EXPANDED_SIZE - 1;

const KnowledgeGraphSidebar = ({
  knowledgeGraphId,
  sidebarNodeStructure,
  nodes,
  graphSearchResults,
  open,
  onClose,
  onVisualGraphToggle,
  anchor,
  loading,
  showData,
  t,
  isEmbedded,
  showVisualGraph,
  handleSearch
}) => {
  const location = useLocation();
  const history = useHistory();

  const { sidebarSize } = useSidebar();
  const showVisualKgToggleLabel = showVisualGraph ? t('sidebar.hide-visual-kg') : t('sidebar.show-visual-kg');
  // The Header with the logo can be a custom height, usually it's 227 (used as fallback shouldn't be needed anyhow)
  // If we are in embedded mode, we don't want to show the logo, so we set the height to 0
  const heightOfLogo = isEmbedded ? 0 : document.getElementById('sidebar-header')?.scrollHeight || 227;
  // Full sidebar is calc(100% - deltaHeightOfSidebar) high
  let deltaHeightOfSidebar = 0;
  if (!isEmbedded) {
    deltaHeightOfSidebar = heightOfLogo;
  }

  const width = isEmbedded ? KnowledgeGraphSidebarDrawerWidth : sidebarSize.width - 1; // -1 is to avoid 1px overlap of the sidebar;

  const toggleSx = {
    '&.MuiIconButton-root': {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: '50% !important',
      color: showVisualGraph ? 'var(--secondary-color)' : 'white',
      padding: '5px'
    }
  };

  const drawerPaperSx = {
    width,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--primary-color)',
    borderRadius: 0,
    height: `calc(100% - ${deltaHeightOfSidebar}px)`,
    top: isEmbedded ? 'unset' : heightOfLogo,
    position: isEmbedded ? 'relative' : 'fixed',
    zIndex: 1
  };

  // specifies whether the sidebar has fully entered (i.e its slide-in transition ended)
  const [sidebarEntered, setSidebarEntered] = useState(false);
  // an array containing the open node and child node (if any)
  const [openNodes, setOpenNodes] = useState([null, null]);
  // the open node (if any)
  const openNode = useMemo(() => openNodes[0], [openNodes]);
  // the open child node (if any)
  const openChildNode = useMemo(() => openNodes[1], [openNodes]);
  // the search input in the sidebar, we want control over it in order to update it if someone uses e.g. visual search
  const searchInput = useRef(null);

  // sets the open node based on the hash
  useEffect(() => {
    // we need to wait for the sidebar to have entered to avoid a misplaced popover
    if (sidebarEntered) {
      // the (shortcut) URI of a resource from the hash (if any)
      const uri = location.hash ? location.hash.substring(1) : null;
      // the node corresponding to the URI (if any)
      const nodeUri = sidebarNodeStructure?.find(node => node.uri === uri)?.uri;
      // the child node corresponding to the URI (if any)
      const childNodeUri =
        sidebarNodeStructure &&
        flatMap(sidebarNodeStructure, node => node.children)?.find(childNode => childNode.uri === uri)?.uri;
      // update the state: only set child node if no node found
      setOpenNodes([nodeUri, nodeUri ? null : childNodeUri]);
    }
  }, [sidebarEntered, location.hash, sidebarNodeStructure, nodes]);

  useEffect(() => {
    if (open && knowledgeGraphId) {
      trackEvent('KG Sidebar Opened', { knowledgeGraphId });
    }
  }, [open, knowledgeGraphId]);

  // unsets the hash if one is set
  const unsetHash = useCallback(() => {
    if (location.hash) {
      history.replace({ search: location.search, hash: null });
    }
  }, [location.hash, history]);

  // Update the search value when there's a query e.g. via visual mode
  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.value = graphSearchResults.query || '';
    }
  }, [graphSearchResults.query, searchInput]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      transitionDuration={50}
      anchor={anchor}
      paperClasses={drawerPaperSx}
      data-test="KnowledgeGraphSidebarHook"
      SlideProps={{
        // called when the slide-in transition ends
        onEntered: _ => setSidebarEntered(true),
        // called when the slide-out transition starts
        onExit: _ => setSidebarEntered(false)
      }}
    >
      {!isEmbedded && (
        <>
          <div className={styles.kgSidebarTitle}>
            <Button
              layout={layouts.veezoo}
              mode="dark"
              classes={{ root: { width: '194px' } }}
              icon={<ArrowBack />}
              onClick={onClose}
              component={LinkWithQuery}
              to={veezooRoutes.chat}
              updateSearchParams={{ kgSidebar: null, visualMode: null }}
              data-knowledgegraph-tutorial="get-back"
              data-test="KnowledgeGraphSidebarClose"
              data-addwidget-tutorial={open ? 'knowledge-graph-sidebar-open' : undefined}
            >
              Knowledge Graph
            </Button>
            <InfoTooltip text={showVisualKgToggleLabel} placement="right">
              <IconButton
                onClick={onVisualGraphToggle}
                sx={toggleSx}
                data-knowledgegraph-tutorial="show-visual-button"
                data-test="KnowledgeGraphSidebarVisualKnowledgeGraphButton"
                aria-label={showVisualKgToggleLabel}
              >
                <KnowledgeGraphIcon />
              </IconButton>
            </InfoTooltip>
          </div>
          <Divider />
          <div>
            <TextField
              ref={searchInput}
              layout={layouts.kgSidebar}
              onChange={handleSearch}
              startIcon={<SearchRoundedIcon />}
              placeholder={`${t('search')}...`}
              title={`${t('search')}...`}
            />
          </div>
          <Divider />
        </>
      )}
      <div className={clsx(styles.scrollableArea, isEmbedded ? styles.isEmbedded : '')}>
        {loading ? (
          <SidebarLoader />
        ) : (
          <SidebarList
            sidebarNodeStructure={sidebarNodeStructure}
            nodes={nodes}
            graphSearchResults={graphSearchResults}
            isEmbedded={isEmbedded}
            openNode={openNode}
            unsetHash={unsetHash}
            openChildNode={openChildNode}
            heightOfLogo={heightOfLogo}
            drawerWidth={isEmbedded ? KnowledgeGraphSidebarDrawerWidth : sidebarSize.width - 1} // -1 is to avoid 1px overlap of the sidebar
            showVisualGraph={showVisualGraph}
            showData={showData}
            t={t}
          />
        )}
      </div>
      {!isEmbedded && (
        <div className={styles.footer}>
          <Button
            layout={layouts.veezoo}
            mode="dark"
            secondaryColor
            classes={{ root: { width: '194px' } }}
            iconSize="20px"
            onClick={onVisualGraphToggle}
            icon={<KnowledgeGraphIcon />}
          >
            {showVisualKgToggleLabel}
          </Button>
        </div>
      )}
    </Drawer>
  );
};

const mapStateToProps = state => ({
  knowledgeGraphId: state.knowledgeGraphMeta.meta.id
});

export default connect(mapStateToProps)(memo(KnowledgeGraphSidebar));

KnowledgeGraphSidebar.defaultProps = {
  anchor: 'left'
};
